import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import { TrailingReturnsPerformanceData } from '../../../types/mutual-fund-detail.types'
import { getTickFormat } from '../../../utils/utils'
import { lightImpactVibrate } from '../../../utils/appBridge'

function transformData(sampleData: any, key: any) {
  return sampleData[key].map((item: { x_value: string | number | Date; y_value: string | number; label: string }) => {
    // Convert x_value from epoch timestamp to date string
    const date = new Date(item.x_value).toISOString().split('T')[0]
    // Use the y_value's value as the new value field
    const value = item.y_value
    const label = item.label
    return { date, value, label }
  })
}

type FundReturnType = {
  amount: string
  returns: string
  date: string
}

type TrailingReturnsGraphPropTypes = {
  tenureLabel: string
  actualData: TrailingReturnsPerformanceData
  onUpdateCurrentValue: (thisFund: FundReturnType, benchmark: FundReturnType, isLastPoint: boolean) => void
}

const TrailingReturnsGraph = ({ tenureLabel, actualData, onUpdateCurrentValue }: TrailingReturnsGraphPropTypes) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const [svgSize, setSvgSize] = useState({ width: 0, height: 250 }) // Initial height can be adjusted
  // Handle resizing based on parent container size
  useEffect(() => {
    const handleResize = () => {
      const parentWidth = (svgRef.current?.parentNode as HTMLElement)?.clientWidth ?? 0
      const parentHeight = (svgRef.current?.parentNode as HTMLElement)?.clientHeight ?? 250 // Default height
      setSvgSize({ width: parentWidth, height: parentHeight })
    }

    // Listen for resize events
    window.addEventListener('resize', handleResize)

    // Call it initially to set size
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const currentFundData = transformData(actualData, 'currFunds')
  const benchmarkFundData = transformData(actualData, 'benchmarkFunds')
  const dataUnavailable = currentFundData.length === 0 || benchmarkFundData.length === 0

  useEffect(() => {
    const svgElement = svgRef.current
    if (!svgElement) return

    const svg = d3.select(svgElement)

    const { width, height } = svgSize
    const margin = { top: 20, right: 5, bottom: 30, left: 5 }

    const xScale = d3
      .scalePoint()
      .domain(currentFundData.map((d: { date: any }) => d.date))
      .range([margin.left, width - margin.right])

    const yScale = d3
      .scaleLinear()
      .domain([
        d3.min([...currentFundData, ...benchmarkFundData], (d) => d.value) * 0.95,
        d3.max([...currentFundData, ...benchmarkFundData], (d) => d.value) * 1.05
      ])
      .range([height - margin.bottom, margin.top])

    // Add the y-axis at the left end with color #EBF2F2
    svg
      .append('g')
      .attr('class', 'y-axis')
      .attr('transform', `translate(${margin.left}, 0)`)
      .call(d3.axisLeft(yScale).tickSize(0)) // No ticks
      .select('.domain')
      .remove()

    // Remove y-axis labels
    svg.selectAll('.y-axis text').remove()

    svg
      .append('line')
      .attr('x1', margin.left)
      .attr('x2', width - margin.right)
      .attr('y1', margin.top)
      .attr('y2', margin.top)
      .attr('stroke', '#EBF2F2')
      .attr('stroke-width', 1)

    // Create three evenly spaced horizontal lines across the chart
    const numLines = 3
    for (let i = 1; i <= numLines; i++) {
      const yPosition = margin.top + (i * (height - margin.top - margin.bottom)) / (numLines + 1)

      svg
        .append('line')
        .attr('x1', margin.left)
        .attr('x2', width - margin.right)
        .attr('y1', yPosition)
        .attr('y2', yPosition)
        .attr('stroke', '#EBF2F2')
        .attr('stroke-width', 1) // Optional: Dashed lines for grid
    }

    if (dataUnavailable) {
      const textPadding = 10
      const message = 'Graph unavailable'

      // Create a group to hold both the rect and the text
      const unavailableGroup = svg.append('g').attr('class', 'unavailable-message')

      // Measure text width by appending the text first
      const textElement = unavailableGroup
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .style('font-family', 'Matter')
        .style('font-size', '12px')
        .style('fill', '#6A6E6E')
        .text(message)

      // Get the text's bounding box to calculate rect size
      const textWidth = textElement.node()?.getBBox().width || 0
      const textHeight = textElement.node()?.getBBox().height || 0

      const boxWidth = textWidth + textPadding * 2
      const boxHeight = textHeight + textPadding

      // Add the background rect before the text in the group
      unavailableGroup
        .insert('rect', 'text') // Insert rect behind the text
        .attr('x', -boxWidth / 2)
        .attr('y', -boxHeight / 2)
        .attr('width', boxWidth)
        .attr('height', boxHeight)
        .attr('rx', 15) // Rounded corners for pill shape
        .attr('ry', 15)
        .style('fill', '#FFFFFF')
        .style('stroke', '#EBF2F2')
        .style('stroke-width', 1)

      // Position the group in the center of the SVG
      unavailableGroup.attr('transform', `translate(${width / 2}, ${height / 2 - 4})`)

      const xAxis = d3
        .axisBottom(xScale)
        .tickValues([])
        .tickFormat((d) => d3.timeFormat('%b %y')(new Date(d)))
        .tickPadding(10)
        .tickSize(0)

      svg
        .append('g')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(xAxis)
        .selectAll('text')

      d3.select('.domain').attr('stroke', '#EBF2F2')
    } else {
      type FundData = { date: string; value: number }

      const line = d3
        .line<FundData>()
        .x((d) => xScale(d.date) ?? 0)
        .y((d) => yScale(d.value) ?? 0)

      const hasNegativeValue =
        currentFundData.some((d: any) => +d.label < 0) || benchmarkFundData.some((d: any) => +d.label < 0)

      if (hasNegativeValue) {
        const defs = svg.append('defs')
        const gradient = defs
          .append('linearGradient')
          .attr('id', 'area-gradient')
          .attr('x1', '0%')
          .attr('y1', '0%')
          .attr('x2', '0%')
          .attr('y2', '100%')

        gradient.append('stop').attr('offset', '0%').attr('stop-color', '#FBEFEF').attr('stop-opacity', 1)
        gradient.append('stop').attr('offset', '50%').attr('stop-color', '#FBEFEF').attr('stop-opacity', 0)

        svg
          .append('path')
          .datum(currentFundData)
          .attr('fill', 'url(#area-gradient)')
          .attr(
            'd',
            d3
              .area()
              .x((d: any) => xScale(d.date) ?? 0)
              .y0(yScale(currentFundData?.[0]?.value))
              .y1(height - margin.bottom)
          )
      }

      svg
        .append('path')
        .datum(currentFundData)
        .attr('fill', 'none')
        .attr('stroke', '#1470CC')
        .attr('stroke-width', 2)
        .attr('d', line)

      svg
        .append('path')
        .datum(benchmarkFundData)
        .attr('fill', 'none')
        .attr('stroke', '#CC5C2D')
        .attr('stroke-width', 2)
        .attr('d', line)

      const overlay = svg
        .append('rect')
        .attr('width', width)
        .attr('height', height)
        .style('fill', 'none')
        .style('pointer-events', 'all')

      // Position focus line and circles at the last currentFundData point initially
      const lastDataPoint = currentFundData[currentFundData.length - 1]
      const lastDataPoint2 = benchmarkFundData[benchmarkFundData.length - 1]

      const focusLine = svg
        .append('line')
        .attr('stroke', 'black')
        .attr('stroke-width', 1)
        .attr('y1', margin.top)
        .attr('y2', height - margin.bottom)
        .style('opacity', 0)

      const focusCircle1 = svg
        .append('circle')
        .attr('r', 3)
        .attr('fill', '#1470CC')
        .attr('stroke', 'white') // Adds a white border
        .attr('stroke-width', 2)
        .style('opacity', 1)

      const focusCircle2 = svg
        .append('circle')
        .attr('r', 3)
        .attr('fill', '#CC5C2D')
        .attr('stroke', 'white') // Adds a white border
        .attr('stroke-width', 2)
        .style('opacity', 1)

      focusCircle1
        .attr('cx', xScale(lastDataPoint.date) ?? 0)
        .attr('cy', yScale(lastDataPoint.value))
        .style('opacity', 1) // Keep it visible initially

      focusCircle2
        .attr('cx', xScale(lastDataPoint2.date) ?? 0)
        .attr('cy', yScale(lastDataPoint2.value))
        .style('opacity', 1) // Keep it visible initially

      const updateFocus = (xPos: number | undefined) => {
        // Calculate the closest currentFundData point based on x position
        const closestIndex = d3
          .bisector<FundData, number>((d) => xScale(d.date) ?? 0)
          .center(currentFundData, xPos ?? 0)
        const d = currentFundData[closestIndex]
        const d2 = benchmarkFundData[closestIndex]

        // Update line and circles
        focusLine
          .attr('x1', xScale(d.date) ?? 0)
          .attr('x2', xScale(d.date) ?? 0)
          .style('opacity', 1)

        focusCircle1
          .attr('cx', xScale(d.date) ?? 0)
          .attr('cy', yScale(d.value))
          .style('opacity', 1)

        const cxFocusCircle2 = d2?.date ? (xScale(d2.date) ?? null) : null
        const cyFocusCircle2 = d2?.value ? (yScale(d2.value) ?? null) : null

        if (cxFocusCircle2 !== null && cyFocusCircle2 !== null)
          focusCircle2.attr('cx', cxFocusCircle2).attr('cy', cyFocusCircle2).style('opacity', 1)

        onUpdateCurrentValue(
          {
            returns: d.label,
            amount: d.value,
            date: d.date
          },
          {
            returns: d2?.label,
            amount: d2?.value,
            date: d2?.date
          },
          false
        )
        // lightImpactVibrate()
      }

      overlay
        .on('touchstart touchmove', function (event) {
          // event.preventDefault() // Prevent default scrolling behavior on touch
          if (event.touches && event.touches.length > 0) {
            const xPos = event.touches[0].clientX - (svgElement?.getBoundingClientRect().left ?? 0)
            updateFocus(xPos)
          }
        })
        .on('touchend', () => {
          // Position focus line and circles at the last currentFundData point
          const lastDataPoint = currentFundData[currentFundData.length - 1]
          const lastDataPoint2 = benchmarkFundData[benchmarkFundData.length - 1]

          focusCircle1
            .attr('cx', xScale(lastDataPoint.date) ?? 0)
            .attr('cy', yScale(lastDataPoint.value))
            .style('opacity', 1) // Keep it visible

          focusCircle2
            .attr('cx', xScale(lastDataPoint2.date) ?? 0)
            .attr('cy', yScale(lastDataPoint2.value))
            .style('opacity', 1) // Keep it visible

          onUpdateCurrentValue(
            {
              returns: lastDataPoint.label,
              amount: lastDataPoint.value,
              date: lastDataPoint.date
            },
            {
              returns: lastDataPoint2.label,
              amount: lastDataPoint2.value,
              date: lastDataPoint2.date
            },
            true
          )

          focusLine.style('opacity', 0)
        })

      const tickValues = []
      const totalPoints = currentFundData.length
      const interval = Math.round((totalPoints - 2) / 5) // Divide currentFundData evenly
      for (let i = 0; i < 6; i++) {
        if (i * interval < totalPoints) tickValues.push(currentFundData[i * interval].date)
      }

      // Ensure the first and last values are always included
      tickValues[0] = currentFundData[0].date
      tickValues[5] = currentFundData[totalPoints - 1].date

      // Define the x-axis with custom tick values
      const xAxis = d3
        .axisBottom(xScale)
        .tickValues(tickValues)
        .tickFormat((d) => d3.timeFormat(getTickFormat(tenureLabel))(new Date(d)))
        .tickPadding(10)
        .tickSize(0)

      svg
        .append('g')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(xAxis)
        .selectAll('text')
        .style('color', (_d, i) => {
          if (i === tickValues.length - 1) return 'black'
          else return '#6A6E6E'
        })
        .style('font-family', 'Matter')
        .style('font-size', '11px')
        .style('text-anchor', (_d, i) => {
          if (i === 0) return 'start'
          if (i === tickValues.length - 1) return 'end'
          return 'middle'
        })

      d3.select('.domain').attr('stroke', '#EBF2F2')
    }

    return () => {
      d3.select(svgElement).selectAll('*').remove()
    }
  }, [svgSize, actualData]) // Re-render when svgSize changes

  return <svg ref={svgRef} width={svgSize.width} height={svgSize.height} role='img' aria-label={`Trailing returns graph for ${tenureLabel ?? 'selected period'}`} />
}

export default TrailingReturnsGraph
